import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './context/AuthContext';
import { UserColorsProvider } from './context/UserColorsContext';
import { ColorGate } from './components/common/ColorChoice';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/layout/Layout';

// Auth pages
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';

// Feature pages
import ShiftTemplatesPage from './pages/ShiftTemplatesPage';
import AvailabilityPage from './pages/AvailabilityPage';
import UserManagementPage from './pages/UserManagementPage';
import RosterPage from './pages/RosterPage';
import MyShiftsPage from './pages/MyShiftsPage';
import DashboardPage from './pages/DashboardPage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import ReportsPage from './pages/ReportsPage';
import SickNotesPage from './pages/SickNotesPage';
import NotificationsPage from './pages/NotificationsPage';
import VacationPage from './pages/VacationPage';
import AdminPage from './pages/AdminPage';
import TipsPage from './pages/TipsPage';
import ProfilePage from './pages/ProfilePage';
import WelcomePage from './pages/WelcomePage';
import OMRolesPage from './pages/OMRolesPage';
import OMTimePage from './pages/OMTimePage';

// ── Placeholder pages ─────────────────────────────────────────────────────────
function PlaceholderPage({ title }) {
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-subtitle">Coming soon</p>
        </div>
      </div>
      <div
        style={{
          background: 'var(--white)',
          borderRadius: 'var(--radius-lg)',
          border: '2px dashed var(--border)',
          padding: '64px 24px',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>
          {title} — not yet implemented
        </p>
        <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>
          This page will be built in an upcoming prompt.
        </p>
      </div>
    </div>
  );
}

// ── WelcomeGate — redirects fresh registrations to /welcome ──────────────────
function WelcomeGate() {
  const { user, loading, markWelcomeSeen } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || !user) return;
    if (user.has_seen_welcome === 1) return;
    if (location.pathname === '/welcome') return;

    const createdAt = new Date(user.created_at).getTime();
    const tenMinutesAgo = Date.now() - 10 * 60 * 1000;

    if (createdAt < tenMinutesAgo) {
      markWelcomeSeen();
      return;
    }
    navigate('/welcome', { replace: true });
  }, [user?.id, user?.has_seen_welcome, loading, location.pathname]);

  return null;
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UserColorsProvider>
        <WelcomeGate />
        <ColorGate />
        <Routes>
          {/* Public auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register/:token" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

          {/* Welcome screen — protected, no layout */}
          <Route
            path="/welcome"
            element={
              <ProtectedRoute>
                <WelcomePage />
              </ProtectedRoute>
            }
          />

          {/* Protected app routes (all inside Layout) */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />

            {/* All-user routes */}
            <Route path="roster" element={<RosterPage />} />
            <Route path="my-shifts" element={<MyShiftsPage />} />
            <Route path="availability" element={<AvailabilityPage />} />
            <Route path="vacation" element={<VacationPage />} />
            <Route path="time-tracking" element={<TimeTrackingPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />

            <Route path="om-time" element={<OMTimePage />} />

            {/* Admin-only routes */}
            <Route
              path="om-roles"
              element={
                <ProtectedRoute adminOnly>
                  <OMRolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="users"
              element={
                <ProtectedRoute adminOnly>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute adminOnly>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="tips"
              element={
                <ProtectedRoute>
                  <TipsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sick-notes"
              element={
                <ProtectedRoute adminOnly>
                  <SickNotesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="shift-templates"
              element={
                <ProtectedRoute adminOnly>
                  <ShiftTemplatesPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/roster" replace />} />
        </Routes>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: '8px',
              fontSize: '0.9rem',
            },
          }}
        />
        </UserColorsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
